import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, type User } from "../api";
import { useToast } from "./Toast";

interface AssignFromFeedProps {
  eventId: number;
  label: string;
}

export function AssignFromFeed({ eventId, label }: AssignFromFeedProps) {
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<User | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    void api<{ users: User[] }>("/api/admin/users")
      .then((data) => setUsers(data.users.filter((u) => u.active && u.role === "user")))
      .catch((err) => push(err instanceof Error ? err.message : "Failed to load users", "error"));
  }, [open, push]);

  useEffect(() => {
    if (!open || pending) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, pending]);

  async function confirmAssign() {
    if (!pending) return;
    setBusy(true);
    try {
      await api(`/api/admin/events/${eventId}/assign`, {
        method: "POST",
        body: JSON.stringify({ userId: pending.id }),
      });
      push(`Assigned to ${pending.username}`, "ok");
      setPending(null);
      setOpen(false);
    } catch (err) {
      push(err instanceof Error ? err.message : "Assign failed", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="assign-inline" ref={rootRef}>
      <button
        type="button"
        className="btn ghost compact"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        Assign
      </button>
      {open && !pending && (
        <div className="assign-menu surface" onClick={(e) => e.stopPropagation()}>
          <p className="muted tiny">
            Assign <strong>{label}</strong> to…
          </p>
          {users.length === 0 && <p className="muted tiny">No standard users yet.</p>}
          <ul>
            {users.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPending(u);
                    setOpen(false);
                  }}
                >
                  {u.username}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pending &&
        createPortal(
          <div
            className="modal-backdrop"
            role="presentation"
            onClick={(e) => {
              e.stopPropagation();
              if (!busy) setPending(null);
            }}
          >
            <div
              className="modal-panel confirm-panel surface"
              role="dialog"
              aria-modal="true"
              aria-labelledby="assign-confirm-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="modal-head">
                <div>
                  <p className="brand">Confirm assignment</p>
                  <h2 id="assign-confirm-title">Assign this account?</h2>
                </div>
              </header>
              <div className="modal-body confirm-body">
                <p>
                  Assign <strong>{label}</strong> to{" "}
                  <strong>{pending.username}</strong>?
                </p>
                <p className="muted tiny">They’ll see it on their account immediately.</p>
                <div className="confirm-actions">
                  <button
                    type="button"
                    className="btn ghost"
                    disabled={busy}
                    onClick={() => setPending(null)}
                  >
                    Cancel
                  </button>
                  <button type="button" disabled={busy} onClick={() => void confirmAssign()}>
                    {busy ? "Assigning…" : "Confirm assign"}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
